
export function getDistanceForJourney(journey) {
    function toRad(x) {
        return (x * Math.PI) / 180;
    }

    function getDistance(lat1, lon1, lat2, lon2) {
        const R = 6371; // km
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const lat1Rad = toRad(lat1);
        const lat2Rad = toRad(lat2);

        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1Rad) * Math.cos(lat2Rad);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c;
        return d;
    }

    const journeyPositions = journey.route.positions

    let totalDistance = 0

    for (let i = 0; i < journeyPositions.length - 1; i++) {
        const currentPos = journeyPositions[i]
        const nextPos = journeyPositions[i + 1]

        const distance = getDistance(currentPos.lat, currentPos.lon, nextPos.lat, nextPos.lon)
        totalDistance += distance
    }

    return totalDistance
}

export function getDistanceForJourneys(journeys) {
    let totalDistance = 0

    for (const journey of journeys) {
        totalDistance += getDistanceForJourney(journey)
    }

    return totalDistance
}

export function getDurationForJourneys(journeys) {
    let totalDuration = 0

    for (const journey of journeys) {
        totalDuration += journey.length
    }

    return totalDuration
}

export function getNoOfCountriesFromJourneys(journeys) {
    const countries = new Set()

    for (const journey of journeys) {
        countries.add(journey.from.country.code)
        countries.add(journey.to.country.code)
    }

    return countries.size
}

export function getTopCountries(journeys) {
    const countries = new Set()

    for (const journey of journeys) {
        countries.add(journey.from.country)
        countries.add(journey.to.country)
    }

    console.log(countries)
    return countries
}

export function getNoOfInternationalJourneys(journeys) {
    let numberOfInternationalJourneys = 0

    for (const journey of journeys) {
        if (journey.from.country.code !== journey.to.country.code) {
            numberOfInternationalJourneys++
        }
    }

    return numberOfInternationalJourneys
}

export function getNoOfDomesticJourneys(journeys) {
    let numberOfDomesticJourneys = 0

    for (const journey of journeys) {
        if (journey.from.country.code === journey.to.country.code) {
            numberOfDomesticJourneys++
        }
    }

    return numberOfDomesticJourneys
}

function getNoOfStationsVisited(journeys) {
    var uniqueStations = {};
    var count = 0;

    for (var i = 0; i < journeys.length; i++) {
        var fromStation = journeys[i].from._id;
        var toStation = journeys[i].to._id;

        if (!uniqueStations[fromStation]) {
            uniqueStations[fromStation] = true;
            count++;
        }

        if (!uniqueStations[toStation]) {
            uniqueStations[toStation] = true;
            count++;
        }
    }

    return count;
}

function getAverageDistanceForJourneys(journeys) {
    if (journeys.length === 0)
        return 0;
    
    return getDistanceForJourneys(journeys) / journeys.length;
}

function getAverageDurationForJourneys(journeys) {
    if (journeys.length === 0)
        return 0;

    return getDurationForJourneys(journeys) / journeys.length;
}

function getIsolatedDuration(journeys) {
    const totalDurationInMinutes = getDurationForJourneys(journeys);

    const isolatedHours = totalDurationInMinutes / 60;
    const isolatedDays = isolatedHours / 24;
    const isolatedWeeks = isolatedDays / 7;
    const isolatedMonths = isolatedWeeks / 4;

    return {
        isolatedMinutes: totalDurationInMinutes,
        isolatedHours: isolatedHours,
        isolatedDays: isolatedDays,
        isolatedWeeks: isolatedWeeks,
        isolatedMonths: isolatedMonths,
    }
}

function getFullyFormattedDuration(journeys) {
    const { isolatedMinutes, isolatedHours, isolatedDays, isolatedMonths } = getIsolatedDuration(journeys);

    return `
        ${Math.floor(isolatedMonths)} ${Math.floor(isolatedMonths) === 1 ? 'month' : 'months'}, 
        ${Math.floor(isolatedDays % 30)} ${Math.floor(isolatedDays % 30) === 1 ? 'day' : 'days'}, 
        ${Math.floor(isolatedHours % 24)} ${Math.floor(isolatedHours % 24) === 1 ? 'hour' : 'hours'} and
        ${Math.floor(isolatedMinutes % 60)} ${Math.floor(isolatedMinutes % 60) === 1 ? 'minute' : 'minutes'}
    `;
}

export function getInsightsForJourneys(journeys, homeCountryCode) {

    const getNoOfJourneysWithinHomeCountry = (journeys) => {
        return journeys.filter(journey =>
            journey.from.country.code === homeCountryCode && journey.to.country.code === homeCountryCode
        ).length;
    };

    const getNoOfJourneysToOrFromAbroad = (journeys) => {
        return journeys.filter(journey =>
        ((journey.from.country.code === homeCountryCode && journey.to.country.code !== homeCountryCode) ||
            (journey.from.country.code !== homeCountryCode && journey.to.country.code === homeCountryCode))
        ).length;
    };

    const getNoOfJourneysOutsideHomeCountry = (journeys) => {
        return journeys.filter(journey =>
            journey.from.country.code !== homeCountryCode && journey.to.country.code !== homeCountryCode
        ).length;
    };

    const getTopCountries = (journeys, limit) => {
        const countryCounts = new Map();

        const incrementCount = (country) => {
            let entry = countryCounts.get(country.code);
            if (entry) {
                entry.count += 1;
            } else {
                countryCounts.set(country.code, { country, count: 1 });
            }
        };

        journeys.forEach(journey => {
            incrementCount(journey.from.country);
            incrementCount(journey.to.country);
        });

        const sortedCountries = Array.from(countryCounts.values())
            .sort((a, b) => b.count - a.count)
            .slice(0, limit);

        return sortedCountries;
    };


    const getDistanceWithinHomeCountry = (journeys) => {
        return journeys.reduce((total, journey) => {
            if (journey.from.country.code === homeCountryCode && journey.to.country.code === homeCountryCode) {
                return total + getDistanceForJourney(journey);
            }
            return total;
        }, 0);
    };

    const getDistanceToOrFromAbroad = (journeys) => {
        return journeys.reduce((total, journey) => {
            if ((journey.from.country.code === homeCountryCode && journey.to.country.code !== homeCountryCode) ||
                (journey.from.country.code !== homeCountryCode && journey.to.country.code === homeCountryCode)) {
                return total + getDistanceForJourney(journey);
            }
            return total;
        }, 0);
    };

    const getDistanceOutsideHomeCountry = (journeys) => {
        return journeys.reduce((total, journey) => {
            if (journey.from.country.code !== homeCountryCode && journey.to.country.code !== homeCountryCode) {
                return total + getDistanceForJourney(journey);
            }
            return total;
        }, 0);
    };

    const getDurationWithinHomeCountry = (journeys) => {
        return journeys.reduce((total, journey) => {
            if (journey.from.country.code === homeCountryCode && journey.to.country.code === homeCountryCode) {
                return total + journey.length;
            }
            return total;
        }, 0);
    };

    const getDurationToOrFromAbroad = (journeys) => {
        return journeys.reduce((total, journey) => {
            if ((journey.from.country.code === homeCountryCode && journey.to.country.code !== homeCountryCode) ||
                (journey.from.country.code !== homeCountryCode && journey.to.country.code === homeCountryCode)) {
                return total + journey.length;
            }
            return total;
        }, 0);
    };

    const getDurationOutsideHomeCountry = (journeys) => {
        return journeys.reduce((total, journey) => {
            if (journey.from.country.code !== homeCountryCode && journey.to.country.code !== homeCountryCode) {
                return total + journey.length;
            }
            return total;
        }, 0);
    };

    const getDistanceDomestic = (journeys) => {
        return journeys.reduce((total, journey) => {
            if (journey.from.country.code === journey.to.country.code) {
                return total + getDistanceForJourney(journey);
            }
            return total;
        }, 0);
    };

    const getDistanceInternational = (journeys) => {
        return journeys.reduce((total, journey) => {
            if (journey.from.country.code !== journey.to.country.code) {
                return total + getDistanceForJourney(journey);
            }
            return total;
        }, 0);
    };

    const getDurationDomestic = (journeys) => {
        return journeys.reduce((total, journey) => {
            if (journey.from.country.code === journey.to.country.code) {
                return total + journey.length;
            }
            return total;
        }, 0);
    };

    const getDurationInternational = (journeys) => {
        return journeys.reduce((total, journey) => {
            if (journey.from.country.code !== journey.to.country.code) {
                return total + journey.length;
            }
            return total;
        }, 0);
    };

    const getNoOfStationsVisitedInHomeCountry = (journeys) => {
        const stations = new Set();
        journeys.forEach(journey => {
            if (journey.from.country.code === homeCountryCode) stations.add(journey.from._id);
            if (journey.to.country.code === homeCountryCode) stations.add(journey.to._id);
        });
        return stations.size;
    };

    const getNoOfStationsVisitedAbroad = (journeys) => {
        const stations = new Set();
        journeys.forEach(journey => {
            if (journey.from.country.code !== homeCountryCode) stations.add(journey.from._id);
            if (journey.to.country.code !== homeCountryCode) stations.add(journey.to._id);
        });
        return stations.size;
    };

    function getTopStations(journeys, limit) {
        var stationCounts = {};

        const incrementCount = (station) => {
            if (stationCounts[station._id]) {
                stationCounts[station._id].count += 1;
            } else {
                stationCounts[station._id] = { station: station, count: 1 };
            }
        };

        for (var i = 0; i < journeys.length; i++) {
            incrementCount(journeys[i].from);
            incrementCount(journeys[i].to);
        }

        var sortedStations = [];
        for (var stationId in stationCounts) {
            sortedStations.push(stationCounts[stationId]);
        }

        sortedStations.sort((a, b) => b.count - a.count);

        return sortedStations.slice(0, limit);
    }


    const formatRoute = (journey) => {
        const stations = [journey.from.name, journey.to.name].sort();
        return `${stations[0]} - ${stations[1]}`;
    };

    const getTopRoutes = (journeys, limit) => {
        const routeCounts = new Map();

        journeys.forEach(journey => {
            const routeDescription = formatRoute(journey);

            let currentCount = routeCounts.get(routeDescription);
            if (currentCount) {
                currentCount.count += 1;
            } else {
                routeCounts.set(routeDescription, { routeDescription, count: 1 });
            }
        });

        const sortedRoutes = Array.from(routeCounts.values())
            .sort((a, b) => b.count - a.count)
            .slice(0, limit);

        return sortedRoutes.map(({ routeDescription, count }) => ({ route: routeDescription, count }));
    };

    const getLongestDistanceJourneys = (journeys, limit) => {
        const routeCounts = new Map();

        journeys.forEach(journey => {
            const routeDescription = formatRoute(journey);

            let currentCount = routeCounts.get(routeDescription);
            if (currentCount) {
                currentCount.count += getDistanceForJourney(journey);
            } else {
                routeCounts.set(routeDescription, { routeDescription, count: getDistanceForJourney(journey) });
            }
        });

        const sortedRoutes = Array.from(routeCounts.values())
            .sort((a, b) => b.count - a.count)
            .slice(0, limit);

        return sortedRoutes.map(({ routeDescription, count }) => ({ route: routeDescription, distance: count }));
    };

    const getLongestDurationJourneys = (journeys, limit) => {
        const routeDurations = new Map();

        journeys.forEach(journey => {
            const routeDescription = formatRoute(journey);
            let currentDuration = routeDurations.get(routeDescription);
            if (currentDuration) {
                currentDuration.totalDuration += journey.length;
            } else {
                routeDurations.set(routeDescription, { route: routeDescription, totalDuration: journey.length });
            }
        });

        return Array.from(routeDurations.values())
            .sort((a, b) => b.totalDuration - a.totalDuration)
            .slice(0, limit)
            .map(({ route, totalDuration }) => ({ route, duration: totalDuration }));
    };

    const getShortestDistanceJourneys = (journeys, limit) => {
        const routeDistances = new Map();

        journeys.forEach(journey => {
            const routeDescription = formatRoute(journey);
            let currentDistance = routeDistances.get(routeDescription);
            if (currentDistance) {
                currentDistance.totalDistance += getDistanceForJourney(journey);
            } else {
                routeDistances.set(routeDescription, { route: routeDescription, totalDistance: getDistanceForJourney(journey) });
            }
        });

        return Array.from(routeDistances.values())
            .sort((a, b) => a.totalDistance - b.totalDistance)
            .slice(0, limit)
            .map(({ route, totalDistance }) => ({ route, distance: totalDistance }));
    };

    const getShortestDurationJourneys = (journeys, limit) => {
        const routeDurations = new Map();

        journeys.forEach(journey => {
            const routeDescription = formatRoute(journey);
            let currentDuration = routeDurations.get(routeDescription);
            if (currentDuration) {
                currentDuration.totalDuration += journey.length;
            } else {
                routeDurations.set(routeDescription, { route: routeDescription, totalDuration: journey.length });
            }
        });

        return Array.from(routeDurations.values())
            .sort((a, b) => a.totalDuration - b.totalDuration)
            .slice(0, limit)
            .map(({ route, totalDuration }) => ({ route, duration: totalDuration }));
    };

    const { isolatedMinutes, isolatedHours, isolatedDays, isolatedWeeks, isolatedMonths } = getIsolatedDuration(journeys);

    const insights = {
        noOfJourneys: journeys.length,

        totalDistance: getDistanceForJourneys(journeys),
        timesAroundTheWorld: (getDistanceForJourneys(journeys) / 40075),
        timesToTheMoon: (getDistanceForJourneys(journeys) / 384400),

        totalDuration: getDurationForJourneys(journeys),
        fullyFormattedDuration: getFullyFormattedDuration(journeys),

        isolatedMinutesDuration: isolatedMinutes,
        isolatedHoursDuration: isolatedHours,
        isolatedDaysDuration: isolatedDays,
        isolatedWeeksDuration: isolatedWeeks,
        isolatedMonthsDuration: isolatedMonths,

        noOfStationsVisited: getNoOfStationsVisited(journeys),
        noOfStationsVisitedInHomeCountry: getNoOfStationsVisitedInHomeCountry(journeys),
        noOfStationsVisitedAbroad: getNoOfStationsVisitedAbroad(journeys),

        topStations: getTopStations(journeys, 10),

        topRoutes: getTopRoutes(journeys, 10),
        longestDistanceJourneys: getLongestDistanceJourneys(journeys, 10),
        longestDurationJourneys: getLongestDurationJourneys(journeys, 10),
        shortestDistanceJourneys: getShortestDistanceJourneys(journeys, 10),
        shortestDurationJourneys: getShortestDurationJourneys(journeys, 10),

        noOfCountriesVisitedByRail: getNoOfCountriesFromJourneys(journeys),
        countriesVisitedByRail: getTopCountries(journeys, 10),
        noOfJourneysWithinHomeCountry: getNoOfJourneysWithinHomeCountry(journeys),
        noOfJourneysToOrFromAbroad: getNoOfJourneysToOrFromAbroad(journeys),
        noOfJourneysOutsideHomeCountry: getNoOfJourneysOutsideHomeCountry(journeys),
        distanceWithinHomeCountry: getDistanceWithinHomeCountry(journeys),
        distanceToOrFromAbroad: getDistanceToOrFromAbroad(journeys),
        distanceOutsideHomeCountry: getDistanceOutsideHomeCountry(journeys),
        durationWithinHomeCountry: getDurationWithinHomeCountry(journeys),
        durationToOrFromAbroad: getDurationToOrFromAbroad(journeys),
        durationOutsideHomeCountry: getDurationOutsideHomeCountry(journeys),

        // noOfDomesticJourneys: getNoOfDomesticJourneys(journeys),
        // noOfInternationalJourneys: getNoOfInternationalJourneys(journeys),
        distanceDomestic: getDistanceDomestic(journeys),
        distanceInternational: getDistanceInternational(journeys),
        durationDomestic: getDurationDomestic(journeys),
        durationInternational: getDurationInternational(journeys),

        averageDistance: getAverageDistanceForJourneys(journeys),
        averageDuration: getAverageDurationForJourneys(journeys),
    }

    return insights;
}

export function getOverviewForJourneys(journeys) {

    const overview = {
        noOfJourneys: journeys.length,
        totalDistance: getDistanceForJourneys(journeys),
        averageDistance: getAverageDistanceForJourneys(journeys),

        totalDuration: getDurationForJourneys(journeys),
        averageDuration: getAverageDurationForJourneys(journeys),
        fullyFormattedDuration: getFullyFormattedDuration(journeys),

        noOfStationsVisited: getNoOfStationsVisited(journeys),
        noOfCountriesVisitedByRail: getNoOfCountriesFromJourneys(journeys),
    }

    return overview;
}
